import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { motion } from "framer-motion";
import {
  PenLine, ShieldCheck, Loader2, ExternalLink, Trash2, Save,
  CheckCircle2, AlertCircle, Lock, FlaskConical,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const ESignatureSettings = () => {
  const { token, user } = useAuth();
  const { t, locale } = useI18n();
  const confirm = useConfirm();
  const isES = locale === "es";

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null); // {configured, provider, config, updated_at}
  const [selectedProvider, setSelectedProvider] = useState(null); // id
  const [formData, setFormData] = useState({}); // dynamic by provider

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/esignature/providers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/esignature/config`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProviders(pRes.data.providers || []);
      setConfig(cRes.data);
      if (cRes.data?.configured) {
        setSelectedProvider(cRes.data.provider);
        // Pre-fill non-secret fields from masked config
        const cfg = cRes.data.config || {};
        const prefilled = {};
        for (const k of Object.keys(cfg)) {
          // skip keys ending with _set (flags)
          if (k.endsWith("_set")) continue;
          // skip masked secret fields (user must re-enter to update)
          if (["api_key", "rsa_private_key", "integration_key"].includes(k)) continue;
          prefilled[k] = cfg[k];
        }
        setFormData(prefilled);
      }
    } catch (e) {
      toast.error(isES ? "Error cargando configuración" : "Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const onSelectProvider = (id) => {
    setSelectedProvider(id);
    const p = providers.find((x) => x.id === id);
    if (!p) return;
    // Seed defaults
    const seed = {};
    for (const f of p.fields) {
      if (f.default !== undefined) seed[f.key] = f.default;
    }
    setFormData(seed);
  };

  const currentProviderMeta = providers.find((p) => p.id === selectedProvider);

  const handleSave = async () => {
    if (!selectedProvider) {
      toast.error(isES ? "Selecciona un proveedor" : "Select a provider");
      return;
    }
    // Validate required
    const p = providers.find((x) => x.id === selectedProvider);
    for (const f of (p?.fields || [])) {
      if (f.required && !formData[f.key]) {
        // If previously saved and secret is masked, allow skipping (server keeps old value? We send null)
        const secretAlreadySet = config?.configured && config?.config?.[`${f.key}_set`];
        if (["api_key", "rsa_private_key", "integration_key"].includes(f.key) && secretAlreadySet) continue;
        toast.error(`${f.label} ${isES ? "es obligatorio" : "is required"}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { provider: selectedProvider, ...formData };
      // Strip undefined/empty strings for secret fields to let backend keep existing (if upsert)
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      const res = await axios.put(`${API}/esignature/config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(isES ? "Configuración guardada" : "Configuration saved");
      await load();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al guardar" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/esignature/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        isES
          ? `Conexión OK (${res.data.account_email || "válido"})`
          : `Connection OK (${res.data.account_email || "valid"})`
      );
    } catch (e) {
      toast.error(extractError(e, isES ? "Prueba fallida" : "Test failed"));
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ title: t("esig.delete_config_title"), description: t("esig.delete_config_desc"), destructive: true }))) return;
    try {
      await axios.delete(`${API}/esignature/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(isES ? "Configuración eliminada" : "Configuration deleted");
      setSelectedProvider(null);
      setFormData({});
      await load();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al eliminar" : "Delete failed"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isOwner = user?.role === "owner";

  return (
    <div className="p-6 space-y-6" data-testid="esignature-settings-page">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <PenLine className="w-8 h-8 text-primary" />
            {isES ? "Firma Electrónica" : "Electronic Signature"}
          </span>
        }
        description={isES
          ? "Configura un proveedor de firma electrónica para enviar documentos a firmar legalmente."
          : "Connect an e-signature provider to send compliance documents for legal signing."}
      />

      {!isOwner && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="py-4 flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-500" />
            <p className="text-sm">
              {isES
                ? "Solo el propietario de la organización puede modificar esta configuración."
                : "Only the organization owner can modify this configuration."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Current status */}
      {config?.configured && (
        <Card className="border-primary/40 bg-primary/5" data-testid="esign-current-status">
          <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium">
                  {isES ? "Proveedor activo" : "Active provider"}:{" "}
                  <span className="text-primary">
                    {providers.find((p) => p.id === config.provider)?.name || config.provider}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {isES ? "Actualizado" : "Updated"}: {config.updated_at && new Date(config.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleTest} disabled={testing}
                data-testid="esign-test-connection">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FlaskConical className="w-4 h-4 mr-1" />}
                {isES ? "Probar conexión" : "Test connection"}
              </Button>
              {isOwner && (
                <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="esign-delete-config">
                  <Trash2 className="w-4 h-4 mr-1" />
                  {isES ? "Eliminar" : "Remove"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Provider picker */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            {isES ? "Selecciona un proveedor" : "Pick a provider"}
          </CardTitle>
          <CardDescription>
            {isES
              ? "Compara proveedores soportados. Obtén tu API key en su panel y pégala abajo."
              : "Compare supported providers. Get your API key from their dashboard and paste it below."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {providers.map((p) => (
            <motion.div
              key={p.id}
              whileHover={{ y: -2 }}
              className={`border rounded-lg p-4 cursor-pointer transition ${
                selectedProvider === p.id ? "border-primary bg-primary/5" : "border-border"
              } ${!p.supported ? "opacity-70" : ""}`}
              onClick={() => isOwner && onSelectProvider(p.id)}
              data-testid={`esign-provider-${p.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <PenLine className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">{p.name}</h3>
                </div>
                {!p.supported && (
                  <Badge variant="outline" className="text-xs">
                    {isES ? "Próximamente" : "Coming soon"}
                  </Badge>
                )}
                {p.supported && (
                  <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                    {isES ? "Disponible" : "Available"}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">{p.description}</p>
              <a
                href={p.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary flex items-center gap-1 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {isES ? "Documentación" : "Docs"} <ExternalLink className="w-3 h-3" />
              </a>
            </motion.div>
          ))}
        </CardContent>
      </Card>

      {/* Dynamic provider form */}
      {selectedProvider && currentProviderMeta && (
        <Card data-testid="esign-config-form">
          <CardHeader>
            <CardTitle>
              {isES ? "Configuración de" : "Configuration for"} {currentProviderMeta.name}
            </CardTitle>
            <CardDescription>
              {isES
                ? "Completa los campos requeridos. Las credenciales quedan cifradas en tu organización."
                : "Fill the required fields. Credentials are stored per-organization."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentProviderMeta.fields.map((f) => {
              const secretSet =
                config?.configured &&
                config?.provider === selectedProvider &&
                config?.config?.[`${f.key}_set`];
              const isSecret = ["api_key", "rsa_private_key", "integration_key"].includes(f.key);
              return (
                <div key={f.key} className="space-y-2">
                  <Label htmlFor={`esign-${f.key}`} className="flex items-center gap-2">
                    {f.label}
                    {f.required && <span className="text-destructive">*</span>}
                    {isSecret && secretSet && (
                      <Badge variant="outline" className="text-xs">
                        {isES ? "Guardado" : "Saved"}
                      </Badge>
                    )}
                  </Label>
                  {f.type === "boolean" ? (
                    <div className="flex items-center gap-3">
                      <Switch
                        id={`esign-${f.key}`}
                        checked={!!formData[f.key]}
                        onCheckedChange={(v) => setFormData({ ...formData, [f.key]: v })}
                        disabled={!isOwner}
                        data-testid={`esign-input-${f.key}`}
                      />
                      <span className="text-sm text-muted-foreground">
                        {formData[f.key] ? (isES ? "Activado" : "Enabled") : (isES ? "Desactivado" : "Disabled")}
                      </span>
                    </div>
                  ) : f.type === "textarea" ? (
                    <Textarea
                      id={`esign-${f.key}`}
                      rows={6}
                      value={formData[f.key] || ""}
                      onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                      placeholder={isSecret && secretSet ? (isES ? "•••• (guardado, deja vacío para no cambiar)" : "•••• (saved, leave empty to keep)") : ""}
                      disabled={!isOwner}
                      data-testid={`esign-input-${f.key}`}
                      className="font-mono text-xs"
                    />
                  ) : (
                    <Input
                      id={`esign-${f.key}`}
                      type={f.type === "password" ? "password" : "text"}
                      value={formData[f.key] || ""}
                      onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                      placeholder={isSecret && secretSet ? (isES ? "•••• (guardado, deja vacío para no cambiar)" : "•••• (saved, leave empty to keep)") : f.default || ""}
                      disabled={!isOwner}
                      data-testid={`esign-input-${f.key}`}
                    />
                  )}
                  {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
                </div>
              );
            })}

            {!currentProviderMeta.supported && (
              <div className="flex items-start gap-2 p-3 border border-amber-500/40 bg-amber-500/5 rounded-md">
                <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                <p className="text-xs">
                  {isES
                    ? "Este proveedor aún no ejecuta envíos reales. Puedes guardar las credenciales; el envío se registrará en modo MOCK hasta que se habilite la integración completa."
                    : "This provider's live send is not implemented yet. Credentials will be saved; sending will run in MOCK mode until full integration ships."}
                </p>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button onClick={handleSave} disabled={saving || !isOwner} data-testid="esign-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {isES ? "Guardar configuración" : "Save configuration"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ESignatureSettings;
